/**
 * OnboardingPage — orchestrates the six-step inspector onboarding flow.
 *
 * State lives here:
 *   • current step
 *   • profile draft (role / regimes / fleets / years)
 *
 * Persistence:
 *   • Profile is mirrored to localStorage on every change so a refresh
 *     mid-tour doesn't lose progress.
 *   • Completion writes `varuna.onboarding.completedAt`; the route guard
 *     in App.tsx uses that to skip auto-redirect on subsequent sign-ins.
 *
 * Re-entry:
 *   • Anyone can revisit this page from `/onboarding` (linked from the
 *     Help drawer) — they'll resume on whichever step they were on, or
 *     start fresh after completion.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';

import WelcomeStep         from './components/WelcomeStep';
import ProfileStep         from './components/ProfileStep';
import DataDepthStep       from './components/DataDepthStep';
import CapabilityTourStep  from './components/CapabilityTourStep';
import SampleInspectionStep from './components/SampleInspectionStep';
import CompletionStep      from './components/CompletionStep';

import {
  STEPS,
  StepId,
  DEFAULT_PROFILE,
  OnboardingProfile,
  LS_KEY_COMPLETED,
  LS_KEY_PROFILE,
} from './types';

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user } = useUser();

  const [step, setStep] = useState<StepId>('welcome');
  const [profile, setProfile] = useState<OnboardingProfile>(() => {
    try {
      const raw = localStorage.getItem(LS_KEY_PROFILE);
      if (raw) return { ...DEFAULT_PROFILE, ...JSON.parse(raw) };
    } catch {
      /* ignore */
    }
    return DEFAULT_PROFILE;
  });

  // Mirror profile to localStorage on every change so a refresh keeps it.
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY_PROFILE, JSON.stringify(profile));
    } catch {
      /* ignore */
    }
  }, [profile]);

  const idx = STEPS.indexOf(step);
  const next = () => setStep(STEPS[Math.min(STEPS.length - 1, idx + 1)]);
  const back = () => setStep(STEPS[Math.max(0, idx - 1)]);

  const complete = () => {
    try {
      localStorage.setItem(LS_KEY_COMPLETED, new Date().toISOString());
    } catch {
      /* ignore */
    }
  };

  const skip = () => {
    complete();
    navigate('/');
  };

  // Use Clerk's first name in the welcome line if available
  const inspectorName = user?.firstName || undefined;

  switch (step) {
    case 'welcome':
      return <WelcomeStep onNext={next} onSkip={skip} inspectorName={inspectorName} />;
    case 'profile':
      return (
        <ProfileStep
          onNext={next}
          onBack={back}
          onSkip={skip}
          profile={profile}
          onChange={setProfile}
        />
      );
    case 'data-depth':
      return <DataDepthStep onNext={next} onBack={back} onSkip={skip} />;
    case 'capabilities':
      return (
        <CapabilityTourStep
          onNext={next}
          onBack={back}
          onSkip={skip}
          /* When the inspector clicks a "See it live" link or the demo
             CTA, mark onboarding complete so the ProtectedRoute gate
             doesn't bounce them straight back to /onboarding. */
          onLeaveTour={complete}
        />
      );
    case 'sample-inspection':
      return <SampleInspectionStep onNext={next} onBack={back} onSkip={skip} />;
    case 'completion':
      return <CompletionStep onBack={back} onComplete={complete} profile={profile} />;
    default:
      return null;
  }
}

/** Has the current user finished onboarding? Used by route guards. */
export function hasCompletedOnboarding(): boolean {
  try {
    return Boolean(localStorage.getItem(LS_KEY_COMPLETED));
  } catch {
    return true; // fail-open if storage is blocked — don't trap the user in a loop
  }
}
